// src/index.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import { I18nextProvider } from "react-i18next";
import { GlobalStyles } from "@mui/material";
import { LicenseInfo } from "@mui/x-license";
import "@fontsource/open-sans";
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import "react-toastify/dist/ReactToastify.css";
import "./assets/styles/bootstrap.scss";

import i18n from "./i18n/i18n";
import AppRoutes from "./routes/AppRoutes";
import { demoMode, host } from "./config/config";
import AuthLoading from "./pages/AuthLoading";
import { useUserStore } from "./stores/userStore";
import { KeycloakProvider } from "./components/Keycloak/keycloakContext";
import { useKeycloakAuth } from "./components/useKeycloakAuth";
import DynamicLocalizationProvider from "./i18n/DynamicLocalizationProvider";

const queryClient = new QueryClient();

const App = () => {
  const { keycloak, isAuthenticated, isAuthenticating, hasCorrectRole, loginErrorMessage } = useKeycloakAuth();

  if (loginErrorMessage) {
    return <div>{loginErrorMessage}</div>;
  }

  // Render a loading indicator while authenticating
  if (isAuthenticating) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <div>Not authenticated yet...</div>;
  }

  return (
    <KeycloakProvider value={{
      isAuthenticated,
      isAuthenticating,
      keycloak,
      login: keycloak?.login,
      logout: keycloak?.logout,
      register: keycloak?.register,
      name: keycloak?.tokenParsed?.preferred_username,
      token: keycloak?.token,
      roles: keycloak?.tokenParsed?.realm_access?.roles || [],
      error: loginErrorMessage || undefined
    }}>
      <AppRoutes />
    </KeycloakProvider>
  );
};

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || "");

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <DynamicLocalizationProvider>
          <BrowserRouter basename={host}>
            <GlobalStyles styles={{ body: { fontFamily: "'Open Sans', sans-serif" } }} />
            {demoMode ? <AppRoutes /> : <App />}
            <ToastContainer
              position="bottom-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </BrowserRouter>
        </DynamicLocalizationProvider>
      </QueryClientProvider>
    </I18nextProvider>
  </React.StrictMode>
);